/** Pure helpers that turn roster state into the grid data the Home tab draws and exports. */

import type { CellData, OfficerMetadata, OperationMessage, OperationMode, OtherZoneDuty, RosterGridData } from "@/types/api";
import {
  formatCounterLabel,
  getOfficerColor,
  hmmmToEndSlot,
  hmmmToSlot,
  naturalSortOfficers,
} from "@/config/modeConfig";
import type { AssignmentRow } from "@/components/OfficerCard";
import { DUTY_FILL } from "@/utils/counterChoice";
import { addQuarterHour } from "@/components/OfficerCard";

/** True when a message has at least one row flagged with the ⚠️ caution symbol —
 * drives the header icon, the Inbox unread badge count, and default read state. */
export function messageHasCaution(msg: OperationMessage): boolean {
  // A new session ID always counts: it is the only route to a roster that was
  // just generated, so it must not sit in the Inbox already looking read.
  if (msg.type === 'new_session') return true;
  return msg.rows.some(r => !!r.existingWindows || !r.success || !!r.partialTiming);
}

export function officerLabelShort(officerId: string, meta?: OfficerMetadata): string {
  if (!meta) return officerId;
  const parts = [meta.deployment, meta.name].filter(Boolean).join(' - ');
  if (!parts) return officerId;
  return parts.length > 22 ? parts.slice(0, 21) + '…' : parts;
}

export function exportSlotToTime(slot: number, startHour: number): string {
  const total = startHour * 60 + slot * 15;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${h.toString().padStart(2, '0')}${m.toString().padStart(2, '0')}`;
}

export function buildOfficerGridDataForExport(
  officer_schedules: Record<string, number[]>,
  officer_metadata: Record<string, OfficerMetadata>,
  time_labels: string[],
  num_slots: number,
  operation_mode: string | undefined,
  startHour: number,
  /** Free-text duties (RosterState.duty_slots): printed as their text, never as a break. */
  duty_slots?: Record<string, Record<string, string>>,
): RosterGridData {
  const SHORT_BREAK_SLOTS = 12;
  const officerIds = naturalSortOfficers(Object.keys(officer_schedules));
  const dutyAt = (id: string, slot: number) => duty_slots?.[id]?.[String(slot)];

  const exportBreaks = new Map<string, Set<number>>();
  for (const [id, schedule] of Object.entries(officer_schedules)) {
    const first = schedule.findIndex(v => v > 0);
    if (first < 0) continue;
    const last = schedule.length - 1 - [...schedule].reverse().findIndex(v => v > 0);
    const slots = new Set<number>();
    let runStart = -1;
    for (let s = first + 1; s < last; s++) {
      const isZero = (schedule[s] ?? 0) === 0 && !dutyAt(id, s);
      if (isZero && runStart === -1) { runStart = s; }
      else if (!isZero && runStart !== -1) {
        if (s - runStart < SHORT_BREAK_SLOTS) for (let r = runStart; r < s; r++) slots.add(r);
        runStart = -1;
      }
    }
    if (runStart !== -1 && last - runStart < SHORT_BREAK_SLOTS) {
      for (let r = runStart; r < last; r++) slots.add(r);
    }
    if (slots.size > 0) exportBreaks.set(id, slots);
  }

  const counter_labels = officerIds.map(id => {
    const schedule = officer_schedules[id] ?? [];
    const first = schedule.findIndex(v => v > 0);
    const last = schedule.length - 1 - [...schedule].reverse().findIndex(v => v > 0);
    const nameLabel = officerLabelShort(id, officer_metadata[id]);
    if (first < 0) return nameLabel;
    return `${nameLabel}\n${exportSlotToTime(first, startHour)}–${exportSlotToTime(last + 1, startHour)}`;
  });

  const cells: CellData[] = officerIds.flatMap((id, row) => {
    const schedule = officer_schedules[id] ?? [];
    const meta = officer_metadata[id];
    const color = getOfficerColor(id, meta);
    const officerBreaks = exportBreaks.get(id) ?? new Set<number>();
    return Array.from({ length: num_slots }, (_, col) => {
      const counterNum = schedule[col] ?? 0;
      const assigned = counterNum > 0;
      const duty = assigned ? undefined : dutyAt(id, col);
      const isBreak = !assigned && !duty && officerBreaks.has(col);
      const isFirstBreak = isBreak && !officerBreaks.has(col - 1);
      return {
        row, col,
        officers: assigned || duty ? [id] : [],
        colors: assigned ? [color] : duty ? [DUTY_FILL] : [],
        duty,
        color: assigned ? color : duty ? DUTY_FILL : isBreak ? '#EAF3DE' : '#f0f0f0',
        text: assigned
          ? formatCounterLabel(counterNum, operation_mode)
          : duty ? duty
          : isFirstBreak
            ? `#\n${exportSlotToTime(col, startHour)}`
            : isBreak ? '#' : '',
        unavailable: false,
        counter: assigned ? counterNum : undefined,
      };
    });
  });

  return { num_counters: officerIds.length, num_slots, cells, time_labels, counter_labels, officer_metadata };
}

export function buildGridData(
  counter_matrix: string[][][],
  officer_metadata: Record<string, OfficerMetadata>,
  start_hour: number,
  operation_mode: string | undefined,
  unavailable_mask: Set<string>,
  num_slots = 48,
  /** The initially-generated roster's officer_schedules (history[0]) — a main
   *  officer's cell whose counter here doesn't match this baseline is flagged
   *  `changed` and drawn in red. Omit to skip the comparison entirely. */
  initial_officer_schedules?: Record<string, number[]>,
  /** officer_key -> {slot (as string) -> note}; painted onto the cell so the
   *  canvas draws the caption. A cell can hold two officers on a handover slot
   *  but only carries one note, so the first officer with one wins. */
  assignment_notes?: Record<string, Record<string, string>>,
  /** Free-text duties (RosterState.duty_slots): not on any counter, carried for the officer popup. */
  duty_slots?: Record<string, Record<string, string>>,
): RosterGridData {
  const num_counters = counter_matrix.length;

  const time_labels: string[] = Array.from({ length: num_slots }, (_, slot) => {
    const h = (start_hour + Math.floor(slot / 4)) % 24;
    const m = (slot % 4) * 15;
    return `${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}`;
  });

  const counter_labels: string[] = Array.from(
    { length: num_counters },
    (_, row) => formatCounterLabel(row + 1, operation_mode),
  );

  const cells: CellData[] = [];
  for (let row = 0; row < num_counters; row++) {
    for (let col = 0; col < num_slots; col++) {
      const officers = counter_matrix[row]?.[col] ?? [];
      const unavailable = unavailable_mask.has(`${row},${col}`);
      const colors = officers.map((id) => getOfficerColor(id, officer_metadata[id]));
      const counterNum = row + 1;
      const changed = !!initial_officer_schedules && officers.some((id) => {
        if (officer_metadata[id]?.officer_type?.toUpperCase() !== 'MAIN') return false;
        return (initial_officer_schedules[id]?.[col] ?? 0) !== counterNum;
      });
      const note = assignment_notes
        ? officers.map((id) => assignment_notes[id]?.[String(col)]).find(Boolean)
        : undefined;
      cells.push({
        row,
        col,
        officers,
        colors,
        color: unavailable ? "#e8e8e8" : "#f8f8f8",
        unavailable,
        changed,
        note,
      });
    }
  }

  return { num_counters, num_slots, cells, time_labels, counter_labels, officer_metadata, assignment_notes, duty_slots };
}

export function patchGridDataForPreview(
  gridData: RosterGridData,
  officerId: string,
  assignments: AssignmentRow[],
  officerMetadata: Record<string, OfficerMetadata>,
  startHour: number,
): RosterGridData {
  const numSlots = gridData.num_slots;
  // Clone cells, stripping the officer from every slot (and the note that came
  // with it — the rows below put back whatever the popup currently holds).
  const newCells: CellData[] = gridData.cells.map(cell => {
    if (!cell.officers.includes(officerId)) return cell;
    const officers = cell.officers.filter(id => id !== officerId);
    const colors = officers.map(id => getOfficerColor(id, officerMetadata[id]));
    return { ...cell, officers, colors, note: undefined };
  });
  // Re-insert the officer at its new positions based on current popup assignments
  for (const row of assignments) {
    if (!row.counter || row.counter === '0' || !row.startTime || !row.endTime) continue;
    const counterIdx = parseInt(row.counter, 10) - 1;
    if (counterIdx < 0 || counterIdx >= gridData.num_counters) continue;
    let startSlot: number, endSlot: number;
    try {
      startSlot = hmmmToSlot(row.startTime, startHour);
      endSlot = hmmmToEndSlot(row.endTime, startHour);
    } catch { continue; }
    for (let slot = startSlot; slot < endSlot && slot < numSlots; slot++) {
      const idx = counterIdx * numSlots + slot;
      const cell = newCells[idx];
      if (!cell || cell.unavailable) continue;
      const officers = [...cell.officers, officerId];
      const colors = officers.map(id => getOfficerColor(id, officerMetadata[id]));
      // The note comes straight off the popup row, so a caption appears as it is
      // typed instead of waiting on the save round trip — same instant path the
      // counter/time pickers already use.
      newCells[idx] = { ...cell, officers, colors, note: row.notes || cell.note };
    }
  }
  return { ...gridData, cells: newCells };
}

/**
 * Collapses an officer's raw per-slot schedule into contiguous same-counter
 * windows — e.g. [0,5,5,5,0,8,8] (slots 1-3 on counter 5, slots 5-6 on counter
 * 8) → two windows. Used by the "Clear Counters" picker so a user can see an
 * officer's actual assignment windows (time + counter) and choose exactly
 * which ones to clear, instead of only clearing everything at once.
 */
export function deriveOfficerScheduleWindows(
  schedule: number[],
  timeLabels: string[],
): Array<{ startSlot: number; endSlot: number; counter: number; start: string; end: string }> {
  const windows: Array<{ startSlot: number; endSlot: number; counter: number; start: string; end: string }> = [];
  let i = 0;
  while (i < schedule.length) {
    const counter = schedule[i] ?? 0;
    if (counter === 0) { i++; continue; }
    let j = i;
    while (j + 1 < schedule.length && (schedule[j + 1] ?? 0) === counter) j++;
    windows.push({
      startSlot: i,
      endSlot: j + 1,
      counter,
      start: timeLabels[i] ?? '0000',
      end: timeLabels[j + 1] ?? addQuarterHour(timeLabels[j] ?? '0000'),
    });
    i = j + 1;
  }
  return windows;
}

/** One officer's stretch on a duty that has its own row on the counter page. */
export interface DutyRowSegment {
  officerId: string;
  startSlot: number;
  /** Exclusive. */
  endSlot: number;
  /** Set for an officer of the OTHER car: not on this roster, so listed for completeness only — nothing
   *  to open or edit. `zone` is the car they belong to, `name` what that roster prints on their row. */
  otherZone?: { zone: OperationMode; sn: number; name: string };
}

/** A duty with its own row under the counters ("B@M CTRL BOOTH") — not a counter, so it is drawn
 *  apart from them and counted nowhere. Overlapping stretches each get a lane of their own. */
export interface DutyRow {
  label: string;
  lanes: DutyRowSegment[][];
}

/**
 * The counter page's duty rows: for each label in `labels`, every officer's run of slots on it,
 * laid into as few lanes as keep them from overlapping. Labels nobody is on are left out.
 */
export function buildDutyRows(
  duty_slots: Record<string, Record<string, string>> | undefined,
  labels: string[],
  otherZone: OtherZoneDuty[] = [],
): DutyRow[] {
  if ((!duty_slots && otherZone.length === 0) || labels.length === 0) return [];
  duty_slots = duty_slots ?? {};
  const rows: DutyRow[] = [];
  for (const label of labels) {
    const segments: DutyRowSegment[] = otherZone
      .filter((d) => d.label === label && d.end_slot > d.start_slot)
      .map((d) => ({
        officerId: `${d.zone}:SN${d.sn}`, startSlot: d.start_slot, endSlot: d.end_slot,
        otherZone: { zone: d.zone, sn: d.sn, name: d.name },
      }));
    for (const officerId of naturalSortOfficers(Object.keys(duty_slots))) {
      const slots = Object.entries(duty_slots[officerId] ?? {})
        .filter(([, text]) => text === label)
        .map(([slot]) => Number(slot))
        .filter((s) => Number.isInteger(s))
        .sort((a, b) => a - b);
      let k = 0;
      while (k < slots.length) {
        let j = k;
        while (j + 1 < slots.length && slots[j + 1] === slots[j] + 1) j++;
        segments.push({ officerId, startSlot: slots[k], endSlot: slots[j] + 1 });
        k = j + 1;
      }
    }
    if (segments.length === 0) continue;
    segments.sort((a, b) => a.startSlot - b.startSlot);
    const lanes: DutyRowSegment[][] = [];
    for (const seg of segments) {
      const lane = lanes.find((l) => l[l.length - 1].endSlot <= seg.startSlot);
      if (lane) lane.push(seg); else lanes.push([seg]);
    }
    rows.push({ label, lanes });
  }
  return rows;
}
